import { useState } from 'react';
import { Menu, User, LogOut, Settings } from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { useAuth } from '@/hooks/useAuth';

/**
 * 헤더 컴포넌트
 * - 사이드바 토글
 * - 로고 및 브랜드 표시
 * - 사용자 프로필 / 로그아웃
 */
export function Header() {
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const { toggleSidebar, isSidebarOpen, isMobile } = useUIStore();
  const { user, logout, isLoggingOut } = useAuth();

  // 로그아웃 처리
  const handleLogout = () => {
    logout();
    // 로그아웃 성공 시 자동으로 로그인 페이지로 리다이렉트됨 (API 클라이언트에서 처리)
  };

  // 프로필 드롭다운 토글
  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  return (
    <header className="fixed top-0 right-0 left-0 z-50 h-16 bg-white border-b border-gray-200">
      <div className="flex justify-between items-center px-4 h-full">
        {/* 좌측 영역 */}
        <div className="flex gap-4 items-center">
          {/* 사이드바 토글 버튼 */}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md transition-colors hover:bg-gray-100"
            aria-label="사이드바 토글"
          >
            <Menu size={20} className="text-gray-600" />
          </button>

          {/* 로고 */}
          <div className="flex gap-2 items-center">
            <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-primary-500">
              <span className="text-sm font-bold text-white">멍</span>
            </div>
            {isSidebarOpen && (
              <h1 className="text-xl font-bold text-primary-500">멍투데이</h1>
            )}
          </div>
        </div>

        {/* 우측 영역 */}
        <div className="flex gap-4 items-center">
          {/* 사용자 프로필 */}
          <div className="relative">
            <button
              onClick={toggleProfile}
              className="flex gap-2 items-center p-2 rounded-md transition-colors hover:bg-gray-100"
            >
              <div className="flex justify-center items-center w-8 h-8 bg-gray-200 rounded-full">
                <User size={16} className="text-gray-600" />
              </div>
              {!isMobile && (
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500">{user?.role}</p>
                </div>
              )}
            </button>

            {/* 프로필 드롭다운 */}
            {isProfileOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-lg">
                <div className="p-3 border-b border-gray-200">
                  <p className="font-medium text-gray-900">{user?.name}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
                <div className="py-1">
                  <button className="flex gap-2 items-center px-3 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-100">
                    <Settings size={16} />
                    설정
                  </button>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="flex gap-2 items-center px-3 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <LogOut size={16} />
                    {isLoggingOut ? '로그아웃 중...' : '로그아웃'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 외부 클릭 시 드롭다운 닫기 */}
      {isProfileOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsProfileOpen(false)}
        />
      )}
    </header>
  );
}
